import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Bot, Send, ShieldCheck, AlertTriangle, Wrench, RotateCcw, Sparkles, IdCard, ListChecks,
  ArrowRightLeft, Moon, ShieldAlert, FlaskConical, Activity as ActivityIcon, CheckCircle2, XCircle,
  Phone, MessageSquare, Gauge, Lock,
} from "lucide-react";
import { Link } from "react-router-dom";
import { PageHeader, Button, Input, Textarea, Select, Checkbox, StatusBadge, LoadingBlock, EmptyState, toast } from "@/components/ds";
import { api, formatApiError, type InboxDetail, type ReceptionistTurn, type AiInteraction } from "@/lib/api";
import { Thread } from "./inbox/Thread";

type Tab = "identity" | "capabilities" | "qualification" | "escalation" | "after_hours" | "guardrails" | "testing" | "quality" | "activity";
const TABS: { key: Tab; label: string; icon: typeof Bot }[] = [
  { key: "identity", label: "Identity", icon: IdCard },
  { key: "capabilities", label: "Capabilities", icon: ListChecks },
  { key: "qualification", label: "Qualification", icon: ListChecks },
  { key: "escalation", label: "Escalation", icon: ArrowRightLeft },
  { key: "after_hours", label: "After Hours", icon: Moon },
  { key: "guardrails", label: "Guardrails", icon: ShieldAlert },
  { key: "testing", label: "Testing", icon: FlaskConical },
  { key: "quality", label: "Quality", icon: Gauge },
  { key: "activity", label: "Activity", icon: ActivityIcon },
];
const STATUS_TONE: Record<string, "green" | "amber" | "red" | "slate" | "blue"> = {
  draft: "slate", testing: "blue", active: "green", paused: "amber", needs_attention: "red",
};
const CAP_LABELS: Record<string, string> = {
  answer_faqs: "Answer FAQs", discuss_services: "Explain services", discuss_approved_pricing: "Discuss approved pricing",
  qualify_leads: "Qualify", book_appointments: "Book",
  bookings_need_confirmation: "Bookings wait for your confirmation", reschedule_appointments: "Reschedule",
  cancel_appointments: "Cancel", send_confirmations: "Send confirmation", transfer_calls: "Transfer", take_messages: "Take message",
};
const toLines = (a?: string[]) => (a ?? []).join("\n");
const fromLines = (s: string) => s.split("\n").map((x) => x.trim()).filter(Boolean);

export default function Receptionist() {
  const [tab, setTab] = useState<Tab>("identity");
  const [center, setCenter] = useState<any>(null);
  const [readiness, setReadiness] = useState<any>(null);

  const load = useCallback(() => {
    api.rcCenter().then(setCenter).catch((e) => toast.error(formatApiError(e)));
    api.rcReadiness().then(setReadiness).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (status: string) => {
    try { const r = await api.rcSetStatus(status); setCenter(r); toast.success(`Status: ${status}`); api.rcReadiness().then(setReadiness); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!center) return <div className="p-2"><LoadingBlock rows={6} /></div>;
  const status = center.config.status as string;

  return (
    <div data-testid="page-receptionist" className="space-y-5">
      <PageHeader title="AI Receptionist" testid="receptionist-header"
        description="One place to configure, test and activate your AI front desk — identity, capabilities, escalation, guardrails and readiness."
        action={
          <div className="flex items-center gap-2">
            <StatusBadge tone={STATUS_TONE[status] ?? "slate"} data-testid="rc-status-badge"><Bot className="h-3.5 w-3.5" /> {status.replace("_", " ")}</StatusBadge>
            <div className="w-40" data-testid="rc-status-select">
              <Select value={status} onChange={setStatus}
                options={center.statuses.map((s: string) => ({ value: s, label: s.replace("_", " ") }))} />
            </div>
          </div>
        } />

      <ReadinessStrip readiness={readiness} onActivate={() => setStatus("active")} />

      <div className="flex gap-1 overflow-x-auto border-b border-slate-200" data-testid="rc-tabs">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`rc-tab-${key}`}
            className={`-mb-px flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
              tab === key ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "identity" && <IdentityTab center={center} onSave={setCenter} />}
      {tab === "capabilities" && <CapabilitiesTab center={center} onSave={setCenter} />}
      {tab === "qualification" && <QualificationTab />}
      {tab === "escalation" && <EscalationTab center={center} onSave={(c: any) => { setCenter(c); api.rcReadiness().then(setReadiness); }} />}
      {tab === "after_hours" && <AfterHoursTab center={center} onSave={setCenter} />}
      {tab === "guardrails" && <GuardrailsTab center={center} onSave={setCenter} />}
      {tab === "testing" && <TestingTab readiness={readiness} llmConfigured={center.llm?.configured} />}
      {tab === "quality" && <QualityTab />}
      {tab === "activity" && <ActivityTab />}
    </div>
  );
}

function Card({ title, icon: Icon, children, testid }: any) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid={testid}>
      <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-800"><Icon className="h-4 w-4 text-blue-500" /> {title}</div>
      {children}
    </div>
  );
}
function Field({ label, children }: any) {
  return <div className="mb-3"><label className="mb-1 block text-xs font-medium text-slate-500">{label}</label>{children}</div>;
}

function ReadinessStrip({ readiness, onActivate }: { readiness: any; onActivate: () => void }) {
  if (!readiness) return null;
  const required = readiness.checks.filter((c: any) => c.required !== false);
  const optional = readiness.checks.filter((c: any) => c.required === false);
  const passed = required.filter((c: any) => c.passed).length;
  const renderCheck = (c: any) => (
    <div key={c.key} className="flex items-center gap-2 text-xs text-slate-600" data-testid={`rc-check-${c.key}`}>
      {c.passed ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-500" /> : <XCircle className="h-3.5 w-3.5 shrink-0 text-slate-300" />}
      <span className="truncate">{c.label}</span>
    </div>
  );
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="rc-readiness">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><ShieldCheck className="h-4 w-4 text-indigo-500" /> Activation readiness — {passed}/{required.length} required</div>
        <Button size="sm" disabled={!readiness.ready} onClick={onActivate} data-testid="rc-activate-btn">Activate receptionist</Button>
      </div>
      <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Required to activate</p>
      <div className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
        {required.map(renderCheck)}
      </div>
      {optional.length > 0 && (
        <>
          <p className="mb-1.5 mt-3 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Optional integrations — connect any time</p>
          <div className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3" data-testid="rc-optional-checks">
            {optional.map(renderCheck)}
          </div>
        </>
      )}
      {readiness.ready
        ? <p className="mt-2 text-xs text-emerald-600">All required checks pass — you can activate now. Optional integrations won't block activation.</p>
        : <p className="mt-2 text-xs text-slate-400">Complete the required checks above before the receptionist can go Active.</p>}
    </div>
  );
}

function IdentityTab({ center, onSave }: any) {
  const [f, setF] = useState<any>({ ...center.config });
  const save = async () => {
    try { onSave(await api.rcIdentity({
      name: f.name, voice_id: f.voice_id, language: f.language, greeting: f.greeting, tone: f.tone,
      speaking_speed: f.speaking_speed, response_length: f.response_length,
      ai_disclosure: f.ai_disclosure, disclosure_text: f.disclosure_text,
    })); toast.success("Identity saved"); } catch (e) { toast.error(formatApiError(e)); }
  };
  const set = (k: string, v: any) => setF((p: any) => ({ ...p, [k]: v }));
  return (
    <Card title="Identity" icon={IdCard} testid="rc-identity">
      <div className="grid gap-x-6 md:grid-cols-2">
        <Field label="Name"><Input value={f.name ?? ""} onChange={(e) => set("name", e.target.value)} data-testid="rc-name" /></Field>
        <Field label="Voice"><Input value={f.voice_id ?? ""} onChange={(e) => set("voice_id", e.target.value)} placeholder="e.g. Ava (provider voice id)" data-testid="rc-voice" /></Field>
        <Field label="Language"><Input value={f.language ?? ""} onChange={(e) => set("language", e.target.value)} placeholder="en-US" data-testid="rc-language" /></Field>
        <Field label="Tone"><Input value={f.tone ?? ""} onChange={(e) => set("tone", e.target.value)} placeholder="friendly-professional" data-testid="rc-tone" /></Field>
        <Field label="Speed"><Select value={f.speaking_speed ?? "normal"} onChange={(v) => set("speaking_speed", v)} testid="rc-speed" options={["slow", "normal", "fast"].map((v) => ({ value: v, label: v }))} /></Field>
        <Field label="Response length"><Select value={f.response_length ?? "medium"} onChange={(v) => set("response_length", v)} testid="rc-length" options={["brief", "medium", "detailed"].map((v) => ({ value: v, label: v }))} /></Field>
      </div>
      <Field label="Greeting"><Textarea rows={2} value={f.greeting ?? ""} onChange={(e) => set("greeting", e.target.value)} data-testid="rc-greeting" /></Field>
      <label className="mb-2 flex items-center gap-2 text-sm text-slate-600"><Checkbox checked={!!f.ai_disclosure} onChange={(v: boolean) => set("ai_disclosure", v)} testid="rc-disclosure-toggle" /> Disclose that callers are speaking with an AI</label>
      <Field label="Disclosure text"><Input value={f.disclosure_text ?? ""} onChange={(e) => set("disclosure_text", e.target.value)} data-testid="rc-disclosure-text" /></Field>
      <Button onClick={save} data-testid="rc-identity-save">Save identity</Button>
    </Card>
  );
}

function CapabilitiesTab({ center, onSave }: any) {
  const caps = center.capabilities;
  const toggle = async (k: string, v: boolean) => {
    try { onSave(await api.rcCapabilities({ [k]: v })); toast.success("Capabilities updated"); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Card title="Capabilities" icon={ListChecks} testid="rc-capabilities">
      <p className="mb-3 text-xs text-slate-400">What the AI is allowed to do. Tools are enforced server-side to match these switches.</p>
      <p className="mb-3 text-xs text-slate-400">
        With <b>Bookings wait for your confirmation</b> on, the AI reserves the time and holds it on your calendar,
        emails you the details, and tells the customer you will call back to confirm. Turn it off and the AI books
        the appointment outright and sends the customer a confirmation itself.
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        {Object.keys(CAP_LABELS).map((k) => (
          <label key={k} className="flex items-center gap-2 rounded-md border border-slate-100 px-3 py-2 text-sm text-slate-700">
            <Checkbox checked={!!caps[k]} onChange={(v: boolean) => toggle(k, v)} testid={`rc-cap-${k}`} /> {CAP_LABELS[k]}
          </label>
        ))}
      </div>
    </Card>
  );
}

function QualificationTab() {
  const [data, setData] = useState<any>(null);
  useEffect(() => { api.rcQualification().then(setData).catch(() => {}); }, []);
  if (!data) return <Card title="Qualification" icon={ListChecks} testid="rc-qualification"><LoadingBlock rows={3} /></Card>;
  return (
    <Card title="Qualification — required questions by service" icon={ListChecks} testid="rc-qualification">
      <p className="mb-3 text-xs text-slate-400">Questions the AI collects, grouped by service. Edit them in <Link to="/app/services" className="text-blue-600 underline">Services → Intake questions</Link>.</p>
      <div className="space-y-3">
        {data.groups.map((g: any) => (
          <div key={g.service_id} className="rounded-md border border-slate-100 p-3" data-testid={`rc-qual-group-${g.service_id}`}>
            <p className="mb-1 text-sm font-semibold text-slate-700">{g.service_name}</p>
            {g.questions.length === 0 ? <p className="text-xs text-slate-400">No questions.</p> : (
              <ol className="space-y-1">
                {g.questions.map((q: any) => (
                  <li key={q.id} className="flex items-center gap-2 text-sm text-slate-600">
                    <span className="text-xs text-slate-400">#{q.order ?? "-"}</span> {q.question || q.label}
                    <StatusBadge tone="slate">{q.question_type || q.type}</StatusBadge>
                    {q.required ? <StatusBadge tone="amber">required</StatusBadge> : <StatusBadge tone="slate">optional</StatusBadge>}
                  </li>
                ))}
              </ol>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}

function EscalationTab({ center, onSave }: any) {
  const [f, setF] = useState<any>({ ...center.escalation });
  const set = (k: string, v: any) => setF((p: any) => ({ ...p, [k]: v }));
  const save = async () => {
    try { onSave(await api.rcEscalation({ ...f, emergency_categories: Array.isArray(f.emergency_categories) ? f.emergency_categories : fromLines(f.emergency_categories || "") })); toast.success("Escalation saved"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Card title="Escalation & transfers" icon={ArrowRightLeft} testid="rc-escalation">
      <p className="mb-3 text-xs text-slate-400">This is the single source of truth for transfer numbers — the Voice line reads from here.</p>
      <div className="grid gap-x-6 md:grid-cols-2">
        <Field label="Human transfer number"><Input value={f.human_transfer_number ?? ""} onChange={(e) => set("human_transfer_number", e.target.value)} data-testid="rc-human-transfer" /></Field>
        <Field label="Emergency transfer number"><Input value={f.emergency_transfer_number ?? ""} onChange={(e) => set("emergency_transfer_number", e.target.value)} data-testid="rc-emergency-transfer" /></Field>
        <Field label="Transfer hours"><Select value={f.transfer_hours ?? "always"} onChange={(v) => set("transfer_hours", v)} testid="rc-transfer-hours" options={["always", "business_hours", "after_hours"].map((v) => ({ value: v, label: v.replace("_", " ") }))} /></Field>
        <Field label="Complaint handling"><Select value={f.complaint_handling ?? "escalate"} onChange={(v) => set("complaint_handling", v)} testid="rc-complaint" options={["transfer", "take_message", "escalate"].map((v) => ({ value: v, label: v.replace("_", " ") }))} /></Field>
        <Field label="Low-confidence action"><Select value={f.low_confidence_action ?? "take_message"} onChange={(v) => set("low_confidence_action", v)} testid="rc-low-confidence" options={["take_message", "transfer", "escalate"].map((v) => ({ value: v, label: v.replace("_", " ") }))} /></Field>
        <Field label="Failed-transfer action"><Select value={f.failed_transfer_action ?? "take_message"} onChange={(v) => set("failed_transfer_action", v)} testid="rc-failed-transfer" options={["take_message", "voicemail", "retry"].map((v) => ({ value: v, label: v.replace("_", " ") }))} /></Field>
      </div>
      <Field label="Emergency categories (one per line)"><Textarea rows={3} value={Array.isArray(f.emergency_categories) ? toLines(f.emergency_categories) : (f.emergency_categories ?? "")} onChange={(e) => set("emergency_categories", e.target.value)} data-testid="rc-emergency-categories" /></Field>
      <Button onClick={save} data-testid="rc-escalation-save">Save escalation</Button>
    </Card>
  );
}

function AfterHoursTab({ center, onSave }: any) {
  const [f, setF] = useState<any>({ ...center.after_hours });
  const set = (k: string, v: any) => setF((p: any) => ({ ...p, [k]: v }));
  const MODES = ["answer_normally", "emergency_only", "take_message", "book_next_available", "transfer_on_call"];
  const save = async () => { try { onSave(await api.rcAfterHours(f)); toast.success("After-hours saved"); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <Card title="After hours" icon={Moon} testid="rc-after-hours">
      <Field label="When closed, the AI should…">
        <Select value={f.mode ?? "take_message"} onChange={(v) => set("mode", v)} testid="rc-after-mode" options={MODES.map((v) => ({ value: v, label: v.replace(/_/g, " ") }))} />
      </Field>
      {f.mode === "transfer_on_call" && (
        <Field label="On-call number"><Input value={f.on_call_number ?? ""} onChange={(e) => set("on_call_number", e.target.value)} data-testid="rc-oncall-number" /></Field>
      )}
      <Button onClick={save} data-testid="rc-after-save">Save after-hours</Button>
    </Card>
  );
}

function GuardrailsTab({ center, onSave }: any) {
  const g = center.guardrails;
  const [f, setF] = useState<any>({
    restricted_topics: toLines(g.restricted_topics), restricted_claims: toLines(g.restricted_claims),
    required_disclosures: toLines(g.required_disclosures), mandatory_human_topics: toLines(g.mandatory_human_topics),
  });
  const set = (k: string, v: any) => setF((p: any) => ({ ...p, [k]: v }));
  const save = async () => {
    try {
      onSave(await api.rcGuardrails({
        restricted_topics: fromLines(f.restricted_topics), restricted_claims: fromLines(f.restricted_claims),
        required_disclosures: fromLines(f.required_disclosures), mandatory_human_topics: fromLines(f.mandatory_human_topics),
      })); toast.success("Guardrails saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const items: [string, string][] = [
    ["restricted_topics", "Restricted topics"], ["restricted_claims", "Restricted claims"],
    ["required_disclosures", "Required disclosures"], ["mandatory_human_topics", "Mandatory human topics"],
  ];
  return (
    <Card title="Guardrails" icon={ShieldAlert} testid="rc-guardrails">
      <p className="mb-3 text-xs text-slate-400">One entry per line. These constrain what the AI may say and force a human handoff on sensitive topics.</p>
      <div className="grid gap-x-6 md:grid-cols-2">
        {items.map(([k, label]) => (
          <Field key={k} label={label}><Textarea rows={4} value={f[k]} onChange={(e) => set(k, e.target.value)} data-testid={`rc-${k}`} /></Field>
        ))}
      </div>
      <Button onClick={save} data-testid="rc-guardrails-save">Save guardrails</Button>
    </Card>
  );
}

function TestingTab({ readiness, llmConfigured }: { readiness: any; llmConfigured?: boolean }) {
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [detail, setDetail] = useState<InboxDetail | null>(null);
  const [interactions, setInteractions] = useState<AiInteraction[]>([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  const [smsBody, setSmsBody] = useState("");
  const [smsResult, setSmsResult] = useState<any>(null);

  const refresh = useCallback(async (cid: string) => {
    const [d, ints] = await Promise.all([api.inboxGet(cid), api.receptionistInteractions(cid)]);
    setDetail(d); setInteractions(ints);
  }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [detail?.messages.length]);

  const send = async () => {
    if (!text.trim() || busy) return;
    const body = text.trim(); setBusy(true); setText("");
    try {
      const res: ReceptionistTurn = await api.receptionistSimulate({ conversation_id: conversationId ?? undefined, text: body });
      setConversationId(res.conversation_id); await refresh(res.conversation_id);
    } catch (e) { toast.error(formatApiError(e)); setText(body); } finally { setBusy(false); }
  };
  const testSms = async () => {
    if (!smsBody.trim()) return;
    try { setSmsResult(await api.smsInbound({ from_number: "(312) 555-9000", body: smsBody.trim() })); toast.success("SMS processed"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const last = interactions[interactions.length - 1];

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_320px]" data-testid="rc-testing">
      <div className="space-y-4">
        <Card title="Text simulator" icon={Sparkles} testid="rc-text-sim">
          <div className="mb-2 flex justify-end"><Button size="sm" variant="outline" onClick={() => { setConversationId(null); setDetail(null); setInteractions([]); }} data-testid="rc-console-reset"><RotateCcw className="h-4 w-4" /> New</Button></div>
          <div className="max-h-72 overflow-y-auto rounded-md border border-slate-100 bg-slate-50 p-3">
            {detail ? <Thread messages={detail.messages} callRecord={detail.call_record} /> : <EmptyState icon={Sparkles} title="Start a test conversation" description="Type a message as the customer." />}
            <div ref={endRef} />
          </div>
          <div className="mt-2 flex items-end gap-2">
            <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Type as the customer…" data-testid="rc-console-input"
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} />
            <Button onClick={send} disabled={busy || !text.trim() || !llmConfigured} data-testid="rc-console-send"><Send className="h-4 w-4" /> {busy ? "…" : "Send"}</Button>
          </div>
        </Card>

        <Card title="Test SMS" icon={MessageSquare} testid="rc-test-sms">
          <div className="flex items-end gap-2">
            <Input value={smsBody} onChange={(e) => setSmsBody(e.target.value)} placeholder="e.g. How much is a tune-up?" data-testid="rc-sms-body" />
            <Button onClick={testSms} disabled={!smsBody.trim()} data-testid="rc-sms-send">Send</Button>
          </div>
          {smsResult && <p className="mt-2 text-sm text-slate-600" data-testid="rc-sms-result">AI <b>{smsResult.action}</b>{smsResult.response ? `: ${smsResult.response}` : ""}</p>}
        </Card>

        <Card title="Test call" icon={Phone} testid="rc-test-call">
          <p className="text-sm text-slate-600">Run a controlled test call and simulate inbound voice on the <Link to="/app/voice" className="text-blue-600 underline">Voice Receptionist</Link> page.</p>
        </Card>
      </div>

      <div className="space-y-4">
        <Card title="Scenario results" icon={FlaskConical} testid="rc-scenarios">
          {readiness?.tests ? (
            <div className="space-y-1.5 text-sm text-slate-600">
              <div className="flex items-center justify-between"><span>Scenarios passed</span><StatusBadge tone={readiness.tests.passed_count >= 3 ? "green" : "amber"}>{readiness.tests.passed_count}</StatusBadge></div>
              <div className="flex items-center justify-between"><span>Critical (emergency/human/out-of-area)</span><StatusBadge tone={readiness.tests.critical_ok ? "green" : "red"}>{readiness.tests.critical_ok ? "pass" : "incomplete"}</StatusBadge></div>
              <Link to="/app/simulator" className="mt-1 inline-block text-xs text-blue-600 underline" data-testid="rc-open-simulator">Open the Simulator →</Link>
            </div>
          ) : <LoadingBlock rows={2} />}
        </Card>

        <Card title="Last turn — knowledge & tools" icon={Wrench} testid="rc-last-turn">
          {!last ? <p className="text-xs text-slate-400">Send a message to see intent, tools and sources.</p> : (
            <div className="space-y-2">
              <div className="flex items-center justify-between"><span className="text-xs text-slate-400">Intent</span><StatusBadge tone="blue">{last.intent}</StatusBadge></div>
              {last.escalated && <StatusBadge tone="red"><AlertTriangle className="h-3 w-3" /> Escalated</StatusBadge>}
              {last.executed_actions?.map((e, i) => (
                <div key={i} className="rounded-md border border-slate-100 bg-slate-50 px-2 py-1" data-testid={`rc-tool-${i}`}>
                  <p className="text-xs font-semibold text-indigo-700">{e.name}</p>
                  <p className="truncate text-[11px] text-slate-500">{JSON.stringify(e.result)}</p>
                </div>
              ))}
              {last.knowledge_sources?.map((s, i) => (
                <div key={i} className="rounded-md bg-blue-50/50 px-2 py-1" data-testid={`rc-source-${i}`}>
                  <p className="text-xs font-medium text-slate-700">{s.title}</p>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function ActivityTab() {
  const [data, setData] = useState<any>(null);
  useEffect(() => { api.rcActivity().then(setData).catch(() => {}); }, []);
  if (!data) return <Card title="Activity" icon={ActivityIcon} testid="rc-activity"><LoadingBlock rows={4} /></Card>;
  const sections: [string, string][] = [
    ["calls", "Calls"], ["sms", "SMS"], ["appointments", "Appointments"],
    ["transfers", "Transfers"], ["failures", "Failures"], ["config_changes", "Configuration changes"],
  ];
  return (
    <div className="grid gap-4 md:grid-cols-2" data-testid="rc-activity">
      {sections.map(([key, label]) => (
        <Card key={key} title={`${label} (${data[key].length})`} icon={ActivityIcon} testid={`rc-activity-${key}`}>
          {data[key].length === 0 ? <p className="text-xs text-slate-400">No recent {label.toLowerCase()}.</p> : (
            <ul className="space-y-1">
              {data[key].slice(0, 6).map((it: any, i: number) => (
                <li key={i} className="truncate text-xs text-slate-600">
                  {it.outcome || it.type || it.action || it.status || it.title || it.service_name || it.direction || "—"}
                  {it.created_at ? <span className="text-slate-300"> · {String(it.created_at).slice(0, 16).replace("T", " ")}</span> : null}
                </li>
              ))}
            </ul>
          )}
        </Card>
      ))}
    </div>
  );
}


const QUALITY_DATE_OPTIONS = [
  { value: "last_7_days", label: "Last 7 days" },
  { value: "last_30_days", label: "Last 30 days" },
  { value: "this_month", label: "This month" },
];

function ScorePill({ score }: { score: number | null | undefined }) {
  const s = score ?? 0;
  const tone = s >= 80 ? "green" : s >= 60 ? "amber" : "red";
  return <StatusBadge tone={tone as any}>{score == null ? "—" : `${score}`}</StatusBadge>;
}

function QualityTab() {
  const [dateFilter, setDateFilter] = useState("last_30_days");
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.quality({ date_filter: dateFilter })); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [dateFilter]);
  useEffect(() => { load(); }, [load]);

  const evaluate = async () => {
    setBusy(true);
    try {
      const r = await api.qualityEvaluate({ date_filter: dateFilter });
      if (r.available) { toast.success(`Evaluated ${r.evaluated ?? 0} interaction(s)`); await load(); }
      else toast.error(r.message || "Evaluation unavailable");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const accept = async (id: string) => {
    try { const r = await api.qualitySuggestionAccept(id); toast.success(r.note || "Saved as draft"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const dismiss = async (id: string) => {
    try { await api.qualitySuggestionDismiss(id); toast.success("Suggestion dismissed"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading || !data) return <Card title="AI Quality Coach" icon={Gauge} testid="rc-quality"><LoadingBlock rows={4} /></Card>;

  if (!data.available) {
    return (
      <Card title="AI Quality Coach" icon={Lock} testid="rc-quality-locked">
        <p className="text-sm text-slate-500">{data.message || "This feature requires the Growth plan or above."}</p>
        <div className="mt-3 flex gap-2">
          <Button size="sm" onClick={() => (window.location.href = "/app/billing")} data-testid="rc-quality-upgrade">View plans</Button>
        </div>
      </Card>
    );
  }

  const ent = data.entitlements || {};
  return (
    <div className="space-y-4" data-testid="rc-quality">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500">Automated QA of your AI's completed calls & chats against approved criteria.</p>
        <div className="flex items-center gap-2">
          <div className="w-40"><Select value={dateFilter} onChange={setDateFilter} options={QUALITY_DATE_OPTIONS} testid="rc-quality-date" /></div>
          {ent.evaluate && data.unevaluated_count > 0 && data.llm_configured && (
            <Button size="sm" onClick={evaluate} disabled={busy} data-testid="rc-quality-evaluate">
              {busy ? "Evaluating…" : `Evaluate ${data.unevaluated_count} new`}
            </Button>
          )}
        </div>
      </div>

      {ent.evaluate && !data.llm_configured && (
        <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700" data-testid="rc-quality-llm-note">AI is not configured on the server — automated evaluation is unavailable.</p>
      )}

      {/* Summary cards */}
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="rc-quality-avg">
          <div className="text-xs font-medium text-slate-500">Average quality score</div>
          <div className="mt-1 font-display text-3xl font-semibold text-slate-900">{data.average_score ?? "—"}<span className="text-base text-slate-400">/100</span></div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="rc-quality-evaluated">
          <div className="text-xs font-medium text-slate-500">Interactions evaluated</div>
          <div className="mt-1 font-display text-3xl font-semibold text-slate-900">{data.evaluated_count ?? 0}</div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="rc-quality-pending">
          <div className="text-xs font-medium text-slate-500">Awaiting evaluation</div>
          <div className="mt-1 font-display text-3xl font-semibold text-slate-900">{data.unevaluated_count ?? 0}</div>
        </div>
      </div>

      {/* Criteria pass rates */}
      <Card title="Criteria pass rates" icon={ListChecks} testid="rc-quality-criteria">
        {(data.criteria || []).length === 0 ? <p className="text-xs text-slate-400">Run an evaluation to see results.</p> : (
          <div className="space-y-2">
            {data.criteria.map((c: any) => (
              <div key={c.key} className="flex items-center gap-3" data-testid={`rc-quality-crit-${c.key}`}>
                <span className="w-44 shrink-0 text-xs text-slate-600">{c.label}</span>
                <div className="h-3 flex-1 overflow-hidden rounded bg-slate-100">
                  <div className="h-full bg-blue-500" style={{ width: `${c.pass_rate ?? 0}%` }} />
                </div>
                <span className="w-20 shrink-0 text-right text-xs text-slate-600">{c.pass_rate == null ? "—" : `${c.pass_rate}%`}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {data.entitlements && !data.entitlements.items && (
        <p className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-700" data-testid="rc-quality-upsell">
          You're seeing quality summaries. Upgrade to <b>Pro</b> for the full AI Quality Coach — per-interaction pass/fail detail and suggested knowledge updates.
        </p>
      )}

      {/* Pro: knowledge suggestions */}
      {ent.suggestions && (
        <Card title={`Suggested knowledge updates (${data.pending_suggestions_count ?? 0})`} icon={Sparkles} testid="rc-quality-suggestions">
          <p className="mb-3 text-xs text-slate-400">Advisory only — accepting creates a <b>draft</b> knowledge entry for your review. Approved knowledge is never changed automatically.</p>
          {(data.suggestions || []).length === 0 ? <p className="text-xs text-slate-400">No pending suggestions.</p> : (
            <div className="space-y-2">
              {data.suggestions.map((s: any) => (
                <div key={s.id} className="rounded-lg border border-slate-200 p-3" data-testid={`rc-quality-sug-${s.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900">{s.title}</p>
                      <StatusBadge tone="slate">{String(s.category).replace(/_/g, " ")}</StatusBadge>
                      <p className="mt-1 line-clamp-2 text-xs text-slate-600">{s.content}</p>
                      {s.rationale && <p className="mt-0.5 text-xs italic text-slate-400">Why: {s.rationale}</p>}
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <Button size="sm" onClick={() => accept(s.id)} data-testid={`rc-quality-accept-${s.id}`}>Save as draft</Button>
                      <Button size="sm" variant="ghost" onClick={() => dismiss(s.id)} data-testid={`rc-quality-dismiss-${s.id}`}>Dismiss</Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Pro: per-interaction detail */}
      {ent.items && (
        <Card title="Recent evaluations" icon={Gauge} testid="rc-quality-items">
          {(data.items || []).length === 0 ? <p className="text-xs text-slate-400">No evaluations yet in this range.</p> : (
            <div className="divide-y divide-slate-100">
              {data.items.map((it: any) => (
                <div key={it.id} className="py-3" data-testid={`rc-quality-item-${it.id}`}>
                  <div className="flex items-center gap-2">
                    <ScorePill score={it.score} />
                    <span className="text-sm font-medium text-slate-800">{it.contact_name || it.contact_phone || "Unknown contact"}</span>
                    <StatusBadge tone="slate">{it.channel}</StatusBadge>
                    <span className="text-xs text-slate-400">{String(it.detected_at).slice(0, 10)}</span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {it.passed_checks.map((c: any) => (
                      <span key={c.key} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700"><CheckCircle2 className="h-3 w-3" />{c.label}</span>
                    ))}
                    {it.failed_checks.map((c: any) => (
                      <span key={c.key} className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs text-red-700" title={c.note}><XCircle className="h-3 w-3" />{c.label}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
